import { useQuery } from '@tanstack/react-query'
import * as api from '../services/retirementEpisodesApi'
import type { RetirementEpisodesRequest } from '../services/retirementEpisodesApi'
import { MAX_EPISODE_REPORT_IDS } from '../types/retirementEpisodes'
import { SLOW, pollUnlessRefused } from '../utils/polling'

/**
 * Retirement episode queries.
 *
 * SLOW polling: the sweep that writes episodes runs once a night at 04:00, and a
 * person's keep is the only other writer — a governance surface, not a live one.
 *
 * Both take `enabled` so an admin page can honour the gate contract
 * (hooks/useLighthouseAdmin.ts): on `denied`, render RestrictedState without
 * fetching an endpoint the caller cannot read.
 */

export function useRetirementEpisodes(
  query: RetirementEpisodesRequest,
  { enabled = true }: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: ['observatory', 'retirementEpisodes', query],
    queryFn: () => api.getRetirementEpisodes(query),
    enabled,
    refetchInterval: pollUnlessRefused(SLOW),
    // Keeps the previous tab's rows on screen while the next loads, so switching
    // state does not flash the skeleton — but ONLY while the report scope is the
    // same. Moving from one report's episodes to another's (Report Detail, a new
    // :reportId) must never show the last report's rows under the next one's
    // name: that is a skeleton, not a placeholder.
    placeholderData: (previous, previousQuery) =>
      sameReportScope(previousQuery?.queryKey, query) ? previous : undefined,
  })
}

/** Whether a previous episodes query was scoped to the same report (or to none) as `query`. */
function sameReportScope(previousKey: readonly unknown[] | undefined, query: RetirementEpisodesRequest): boolean {
  const previous = previousKey?.[2] as RetirementEpisodesRequest | undefined
  return (previous?.reportId || null) === (query.reportId || null)
}

/**
 * The latest episode of each report on a page of the candidate list.
 *
 * Enabled only for 1..50 ids: zero asks nothing worth a request, and more than the
 * server's cap is a 400 — the caller passes one page, which is at most 50.
 */
export function useEpisodeStates(
  reportIds: string[],
  { enabled = true }: { enabled?: boolean } = {},
) {
  const inRange = reportIds.length >= 1 && reportIds.length <= MAX_EPISODE_REPORT_IDS
  return useQuery({
    queryKey: ['observatory', 'retirementEpisodeStates', reportIds.join(',')],
    queryFn: () => api.getEpisodeStates(reportIds),
    enabled: enabled && inRange,
    refetchInterval: pollUnlessRefused(SLOW),
  })
}
